
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import Point from "slotworks/utils/geom/point";
import * as TweenJS from "slotworks/utils/tween";

export default class LinesComponent {

    /**
     * It is common to reuse a line graphic for inverted / mirrors lines (flips on x or y axis)
     * To flip lines, set it's index (0 indexed) on this map and the axis or axes you want to flip
     * e.g. lineFlips.set(2, new Point(1, 0)); // Flips line 2 on the x axis
     * Line sprites only, this is redundant for runtime drawn lines as no flipping would be required
     */
    public static lineFlips: Map<number, Point> = new Map<number, Point>();
    // Draw lines through code if a sprite is not present
    public static DRAW_LINES: boolean = true;
    // Mask to pan over lines when highlighing, useful for "shimmers" across a line
    public static HIGHLIGHT_MASK?: string;

    private lines: Array<PIXI.Graphics | PIXI.Sprite>;
    private highlightLines: Array<PIXI.Graphics | PIXI.Sprite>;
    private pips: Array<Array<PIXI.Graphics | PIXI.Sprite>>;

    private activeHighlightMasks: PIXI.Sprite[] = [];

    constructor(lineDefinitions: Point[][]) {
        this.draw(lineDefinitions);
    }

    public draw(lineDefinitions: Point[][]) {
        if (this.lines && this.lines.length) {
            for (const line of this.lines) {
                if (line instanceof PIXI.Graphics) {
                    line.clear();
                }

                Layers.Paylines.remove(line);
            }
        }

        this.lines = [];
        this.highlightLines = [];

        for (let lineIndex = 0; lineIndex < lineDefinitions.length; lineIndex++) {
            const lineDefinition = lineDefinitions[lineIndex];

            let line: PIXI.Graphics | PIXI.Sprite;
            let highlightLine: PIXI.Graphics | PIXI.Sprite;

            const lineSprite = Layers.Paylines.getSprite("line_" + (lineIndex + 1));
            let highlightLineSprite = Layers.Paylines.getSprite("highlight_line_" + (lineIndex + 1));

            if (!highlightLineSprite) {
                highlightLineSprite = lineSprite;
            }

            if (lineSprite) {
                // Graphical lines (assetworks)
                line = lineSprite;
                highlightLine = highlightLineSprite;

                if (LinesComponent.lineFlips.has(lineIndex)) {
                    this.flip(lineIndex, line);
                    if (line !== highlightLine) {
                        this.flip(lineIndex, highlightLine);
                    }
                }
            } else {
                // Draw them ourselves
                if (LinesComponent.DRAW_LINES) {
                    line = this.drawLine(lineDefinition, 10, 0xff0000, 0.2);
                    this.drawLine(lineDefinition, 8, 0xff0000, 0.4, line);
                    this.drawLine(lineDefinition, 6, 0xff0000, 0.6, line);
                    this.drawLine(lineDefinition, 2, 0xffffff, 1, line);
                } else {
                    line = new PIXI.Graphics();
                }

                highlightLine = line;
            }

            line.visible = false;
            highlightLine.visible = false;

            this.lines.push(line);
            this.highlightLines.push(highlightLine);

            Layers.Paylines.add(line);
            Layers.Paylines.add(highlightLine);
        }

        this.drawPips(lineDefinitions);
    }

    public win(id: number, reverse: boolean = false) {
        const line = this.lines[id - 1];

        this.show(line, reverse);
        this.showPips(id);
    }

    public highlight(id: number, reverse: boolean = false) {
        if (LinesComponent.HIGHLIGHT_MASK) {
            this.maskHighlight(id);
        }

        const line = this.highlightLines[id - 1];
        this.show(line, reverse);
    }

    public hideAllLines() {
        for (const line of this.lines) {
            line.visible = false;
        }
        for (const line of this.highlightLines) {
            line.visible = false;
        }
        this.highlightLines.forEach((value, index) => this.clearHighlightMask(index));

        this.pips.forEach((pipSet) => {
            pipSet.forEach((pip) => {
                pip.visible = false;
            });
        });
    }

    public tint(id: number, color: number) {
        this.lines[id - 1].tint = color;
        this.highlightLines[id - 1].tint = color;
    }

    public filter(id: number, filters: Array<PIXI.Filter<any>>) {
        this.lines[id - 1].filters = this.highlightLines[id - 1].filters = null;
        this.lines[id - 1].filters = this.highlightLines[id - 1].filters = filters;
    }

    private show(line: PIXI.Graphics | PIXI.Sprite, reverse: boolean = false) {
        line.visible = true;

        if (line instanceof AnimatedSprite) {
            line.reverse = reverse;
            line.gotoAndPlay();
        }
    }

    private showPips(id: number) {
        for (const pips of this.pips) {
            for (const pip of pips) {
                pip.visible = true;
            }
        }

        const lineIndex = id - 1;
        if (lineIndex < this.pips.length) {
            const activePips = this.pips[lineIndex];

            for (const activePip of activePips) {
                if (activePip instanceof AnimatedSprite) {
                    activePip.gotoAndPlay();

                    Layers.SymbolAnimations.add(activePip);
                }
            }
        }
    }

    private flip(id: number, line: PIXI.Sprite) {
        const flip = LinesComponent.lineFlips.get(id);
        if (flip.x) {
            line.scale.x = -line.scale.x;
        }
        if (flip.y) {
            line.scale.y = -line.scale.y;
            line.y += line.height;
        }
    }

    private drawLine(lineDefinition: Point[], weight: number, color: number, alpha: number, graphics?: PIXI.Graphics): PIXI.Graphics {
        if (!graphics) {
            graphics = new PIXI.Graphics();
        }

        graphics.lineStyle(weight, color, alpha);

        const firstPosition = lineDefinition[0];
        const firstSymbolPosition = Layers.MatrixContent.getPosition("symbol_" + (firstPosition.x + 1) + "_" + (firstPosition.y + 1));
        graphics.moveTo(firstSymbolPosition.x, firstSymbolPosition.y + firstSymbolPosition.height * 0.5);

        for (const point of lineDefinition) {
            const x = point.x + 1;
            const y = point.y + 1;

            const symbolPosition = Layers.MatrixContent.getPosition(`symbol_${x}_${y}`);

            graphics.lineTo(symbolPosition.x + symbolPosition.width * 0.5, symbolPosition.y + symbolPosition.height * 0.5);
        }

        const lastPosition = lineDefinition[lineDefinition.length - 1];
        const lastSymbolPosition = Layers.MatrixContent.getPosition("symbol_" + (lastPosition.x + 1) + "_" + (lastPosition.y + 1));
        graphics.lineTo(lastSymbolPosition.x + lastSymbolPosition.width, lastSymbolPosition.y + lastSymbolPosition.height * 0.5);

        return graphics;
    }

    private drawPips(lineDefinitions: Point[][]) {

        this.pips = [];

        for (let lineIndex = 0; lineIndex < lineDefinitions.length; lineIndex++) {
            const pipLeft = Layers.Paylines.getAnimatedSprite(`line_${lineIndex + 1}_number_left`);
            const pipRight = Layers.Paylines.getAnimatedSprite(`line_${lineIndex + 1}_number_right`);
            const pips = [];

            if (pipLeft) {
                pipLeft.visible = false;
                pips.push(pipLeft);
            }
            if (pipRight) {
                pipRight.visible = false;
                pips.push(pipRight);
            }

            this.pips.push(pips);
        }
    }

    private maskHighlight(id: number): void {

        this.clearHighlightMask(id);

        const lineSprite = Layers.Paylines.getSprite("line_" + (id));
        lineSprite.visible = true;
        const highlightSprite = Layers.Paylines.getSprite("highlight_line_" + (id));
        const mask = GraphicsManager.createSprite(LinesComponent.HIGHLIGHT_MASK);
        mask.height = lineSprite.height;
        highlightSprite.parent.addChild(mask);
        highlightSprite.mask = mask;

        mask.x = lineSprite.x - (mask.width);
        mask.y = lineSprite.y;

        this.addHighlightMask(id, highlightSprite);

        new TweenJS.Tween(mask)
            .to({ x: lineSprite.x + (highlightSprite.width) }, 1000)
            .onComplete(() => {
                this.clearHighlightMask(id);
                lineSprite.visible = false;
            })
            .start();
    }

    private addHighlightMask(id: number, sprite: PIXI.Sprite) {
        this.activeHighlightMasks[id] = sprite;
    }

    private clearHighlightMask(id: number) {
        if (this.activeHighlightMasks[id]) {
            const highlight = this.activeHighlightMasks[id];
            const mask = highlight.mask;

            TweenJS.removeAll(mask);
            this.activeHighlightMasks[id] = null;
            highlight.mask = null;
            mask.destroy();
        }
    }
}
